import { PRESENT_VALUE_SENTINEL } from '../../../types/QueryBuilder';
import type { Operator } from '../../../types/QueryBuilder';

import type { FieldType } from './fieldType';
import { isPresentSentinelValue } from './presentSentinel';

/**
 * Display labels for each operator.
 * Typed as `Record<Operator, string>` so TypeScript will error if a new
 * operator is added to the `Operator` union without a corresponding entry here.
 */
export const OPERATOR_DISPLAY_MAP = {
  '=': 'is',
  '!=': 'is not',
  '>': 'is greater than',
  '>=': 'is greater than or equal to',
  '<': 'is less than',
  '<=': 'is less than or equal to',
  '^=': 'starts with',
  '=^': 'ends with',
  '**': 'contains',
  '!**': 'does not contain',
  '//': 'matches',
  '!//': 'does not match',
} as const satisfies Record<Operator, string>;

/**
 * Short helper text shown under each operator in the autocomplete picker.
 * Wording tracks Observability's established operator descriptions
 * (`packages/observability/.../operatorDescriptions.ts`) where the symbols
 * overlap, but stays consumer-neutral (no "Search for" / "Filter for") so
 * shared dataviz callers can reuse it. Starts/ends-with have no Observability
 * counterpart; those descriptions mirror the contains/substring pattern.
 */
export const OPERATOR_DESCRIPTION_MAP = {
  '=': 'Match exact values.',
  '!=': 'Exclude exact matches.',
  '>': 'Match values greater than a number.',
  '>=': 'Match values greater than or equal to a number.',
  '<': 'Match values less than a number.',
  '<=': 'Match values less than or equal to a number.',
  '^=': 'Match values that start with a prefix.',
  '=^': 'Match values that end with a suffix.',
  '**': 'Match values that contain a substring.',
  '!**': 'Exclude values that contain a substring.',
  '//': 'Match values against a regular expression.',
  '!//': 'Exclude values that match a regular expression.',
} as const satisfies Record<Operator, string>;

/** Flat options array derived from the display/description maps, ready for AutocompletePopover. */
export const OPERATOR_OPTIONS = (Object.keys(OPERATOR_DISPLAY_MAP) as Operator[]).map((value) => ({
  value,
  displayName: OPERATOR_DISPLAY_MAP[value],
  description: OPERATOR_DESCRIPTION_MAP[value],
}));

/** Operator-picker value for unary "exists" (`field:=:__present__`). */
export const EXISTS_OPERATOR_OPTION = PRESENT_VALUE_SENTINEL;
/** Operator-picker value for unary "does not exist" (`field:!=:__present__`). */
export const NOT_EXISTS_OPERATOR_OPTION = '__not_present__';

/** Unary exists operators shown at the top of the operator picker, not the value list. */
export const EXISTS_OPERATOR_OPTIONS = [
  {
    value: EXISTS_OPERATOR_OPTION,
    displayName: 'exists',
    description: 'Match if the field is set, regardless of value.',
  },
  {
    value: NOT_EXISTS_OPERATOR_OPTION,
    displayName: 'does not exist',
    description: 'Match if the field is missing.',
  },
];

export function isExistsOperatorOption(value: string): boolean {
  return value === EXISTS_OPERATOR_OPTION || value === NOT_EXISTS_OPERATOR_OPTION;
}

const OPERATOR_LABELS_LONGEST_FIRST = [...OPERATOR_OPTIONS, ...EXISTS_OPERATOR_OPTIONS]
  .map((option) => option.displayName)
  .sort((a, b) => b.length - a.length);

export function takeLeadingOperatorDraft(text: string): { operatorText: string; rest: string } | null {
  const lower = text.toLowerCase();
  for (const label of OPERATOR_LABELS_LONGEST_FIRST) {
    const prefix = `${label.toLowerCase()} `;
    if (lower.startsWith(prefix)) {
      return { operatorText: text.slice(0, prefix.length), rest: text.slice(prefix.length) };
    }
  }
  return null;
}

/** Prepend exists / does not exist when the bar allows them. */
export function withExistsOperatorOptions<T extends { value: string }>(
  ops: T[],
  allowExists: boolean,
): Array<T | (typeof EXISTS_OPERATOR_OPTIONS)[number]> {
  return allowExists ? [...EXISTS_OPERATOR_OPTIONS, ...ops] : ops;
}

/**
 * Operator cell label. Present-sentinel filters read as "exists" / "does not exist"
 * instead of "is" / "is not".
 */
export function getOperatorCellDisplay(operator: Operator, filterValue: unknown, opts?: { oneOf?: boolean }): string {
  if (isPresentSentinelValue(filterValue)) {
    return operator === '!=' ? 'does not exist' : 'exists';
  }
  return getOperatorDisplay(operator, opts);
}

/**
 * Rendered operator label. Appends " one of" when `oneOf` is set (a single-value field with more
 * than one chosen value, e.g. "is one of"). Display only — the token/backend operator is unchanged,
 * so OPERATOR_DISPLAY_MAP / OPERATOR_OPTIONS (the operator picker) keep their base labels.
 */
export function getOperatorDisplay(operator: Operator, opts?: { oneOf?: boolean }): string {
  return opts?.oneOf ? `${OPERATOR_DISPLAY_MAP[operator]} one of` : OPERATOR_DISPLAY_MAP[operator];
}

/**
 * Operators that only ever carry a single value. The numeric comparisons (`>`, `>=`, `<`, `<=`)
 * are meaningless against a set — "greater than 5 AND 10" doesn't render as a comma-separated
 * multiselect, and the metrics backend lists them in singleValueOperators / SINGLE_VALUE_OPS and
 * 400s when given multiple values. The value picker renders single-select for these, and the
 * operator label never gets the "… one of" suffix (e.g. no "is less than or equal to one of").
 */
export const SINGLE_VALUE_OPERATORS = new Set<Operator>(['>', '>=', '<', '<=']);

export const EQUALITY_OPERATORS: Operator[] = ['=', '!='];
export const STRING_OPERATORS: Operator[] = ['=', '!=', '**', '!**', '^=', '=^', '//', '!//'];
export const NUMERIC_OPERATORS: Operator[] = ['=', '!=', '>', '>=', '<', '<='];

/** Operators that make semantic sense for each field type. */
export const COMPATIBLE_OPERATORS: Record<FieldType, Operator[]> = {
  BOOLEAN: EQUALITY_OPERATORS,
  STRING: [...STRING_OPERATORS, '>', '>=', '<', '<='],
};

/**
 * Operators that can semantically pair with the `__present__` value sentinel
 * ("match any value, just require the field to be set"). Used by:
 *   - useQueryBuilderState's UPDATE_FILTER_OP reducer, to drop the sentinel
 *     when the user switches to an op that doesn't accept it (and no explicit
 *     replacement value was provided).
 *
 * Keep this list and the consumer behavior in sync — `=` / `!=` round-trip
 * cleanly to/from the present mode, and `**` (contains) treats "present"
 * as a synonym for "non-empty," which is acceptable for that op too.
 */
export const PRESENT_COMPATIBLE_OPERATORS = new Set<Operator>(['=', '!=', '**']);

export function isOperatorCompatible(op: Operator, type: FieldType): boolean {
  return COMPATIBLE_OPERATORS[type].includes(op);
}

/** The default (first) compatible operator for a given field type. */
export function getDefaultOperator(type: FieldType): Operator {
  return COMPATIBLE_OPERATORS[type][0];
}
